import axios from 'axios';
import { API_ENDPOINT, STORAGE_URL } from '@/configs';
import { logoutServices, syncStorageIntoStateService } from '../services';
import { getTenantId } from '../functions';
import { localStorageLabels } from '../constants';
import { platform } from '@/platform';

const headers = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

/**
 * Base API instance for general requests.
 * - Sets the base URL from environment config.
 * - The screen authenticates with its "Authorization: Screen" header. The hosted player also
 *   sends credentials; packaged apps can't (their origin is file://, app:// or the Android asset
 *   origin, which the API only allows without credentials).
 * - Default headers for JSON content.
 */
export const API = axios.create({
  baseURL: API_ENDPOINT,
  withCredentials: !platform.isPackaged,
  headers,
});

/**
 * Storage API instance.
 * - Dynamically constructs the base URL using the tenant ID.
 * - Used for interacting with Cloudflare R2 storage.
 */
export const STORAGE_API = axios.create({
  baseURL: STORAGE_URL + getTenantId() + '/',
  headers,
});

API.interceptors.request.use(
  (config) => {
    // The paired screen authenticates with its access token; the server derives the tenant
    // from it (no tenant header is sent or trusted).
    const screenInfo = syncStorageIntoStateService(
      localStorageLabels.DEVICE_INFO,
    );
    const accessToken = screenInfo?.accessToken;

    if (accessToken) config.headers['Authorization'] = 'Screen ' + accessToken;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

API.interceptors.response.use(
  (response) => Promise.resolve(response),
  async (error) => {
    if (error?.response?.status === 401) {
      Promise.resolve(logoutServices()).then(() => platform.restart());
    }

    return Promise.reject(error);
  },
);
